import Vue from 'vue';
import store from './store/store';
import { LoaderPlugin } from 'vue-google-login';
import axiosRetry from 'axios-retry';

Vue.use(LoaderPlugin, {
    client_id: '467739130810-1pp7v3i7b7655h389dgcs9p0elt8vt1f.apps.googleusercontent.com'
});

// globally override console
function createStack(error: Error) 
{
    const stack = (error.stack || '').split('\n');
    stack[0] = stack[0].replace('Error: ', '');
    return stack;
}
function safeMessage(message: any) 
{
    try 
    {
        JSON.stringify(message);
        return message;
    }
    catch (_) 
    {
        return `${message}`;
    }
}
const originalConsole = console;
const maxCacheLength = 30;
const logCache = <any[]>[];
const warnCache = <any[]>[];
const errorCache = <any[]>[];
console = {} as Console;
console.log = (message?: any, ...optionalParams: any[]) => 
{
    // originalConsole.trace();
    originalConsole.log(message, ...optionalParams);
    while (logCache.length >= maxCacheLength) 
    {
        logCache.shift();
    }
    logCache.push({ message: safeMessage(message), stack: createStack(new Error("StackLog")) });
};
console.warn = (message?: any, ...optionalParams: any[]) => 
{
    originalConsole.warn(message, ...optionalParams);
    while (warnCache.length >= maxCacheLength) 
    {
        warnCache.shift();
    }
    warnCache.push({ warning: safeMessage(message), stack: createStack(new Error("StackLog")) });
};
console.error = (message?: any, ...optionalParams: any[]) => 
{
    originalConsole.error(message, ...optionalParams);
    while (errorCache.length >= maxCacheLength) 
    {
        errorCache.shift();
    }
    errorCache.push({ error: safeMessage(message), stack: createStack(new Error("StackLog")) });
};
(window as any).console = console;
(window as any).logCache = logCache;
(window as any).warnCache = warnCache;
(window as any).errorCache = errorCache;

import App from './App.vue';
import router from './router';
import getParameterByName from './util/QueryParams';
import { GetSID, SetSID, GetUser, SessionIsValid, ClearSession } from './session';

import '@/middleware/class-component-hooks';

import ElementQueries from 'css-element-queries';
ElementQueries.ElementQueries.listen();

import Vuelidate from 'vuelidate';
Vue.use(Vuelidate);

import BootstrapVue from 'bootstrap-vue';
//import 'bootstrap/dist/css/bootstrap.css'
//import 'bootstrap-vue/dist/bootstrap-vue.css'
// todo
// cssVars()
// Enables Bootstrap globally
Vue.use(BootstrapVue);

import { LMap, LTileLayer, LMarker, LIcon } from 'vue2-leaflet';
import { Icon } from 'leaflet';
import 'leaflet/dist/leaflet.css';

Vue.component('l-map', LMap);
Vue.component('l-tile-layer', LTileLayer);
Vue.component('l-marker', LMarker);
Vue.component('l-icon', LIcon);

// this part resolve an issue where the markers would not appear (https://korigan.github.io/Vue2Leaflet/#/quickstart.md)
delete (Icon.Default.prototype as any)._getIconUrl;

Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png')
});

import { IsEmbedBuild } from '@/util/embed_schema';
import VueAnalytics from 'vue-analytics';

Vue.use(VueAnalytics, {
    id: 'UA-147322861-1',
    disabled: false,
    /*debug: {
        sendHitTask: process.env.NODE_ENV === 'production'
    }*/
    router
});

import Recomputed from 'vue-recomputed';
Vue.use(Recomputed as any);

// Allows for reactivity on refs in a component
// Used for several components, solves some janky
// hacks.  Initially imported for map view.
import { ReactiveRefs } from 'vue-reactive-refs';
Vue.use(ReactiveRefs);

// Enables this.$http globally
import axios from 'axios';
import VueAxios from 'vue-axios';
Vue.use(VueAxios, axios);

import { APIBaseURL } from '@/constants/environment';
Vue.axios.defaults.baseURL = APIBaseURL;

const isNetworkError = (error: any) =>
    error && // just to make sure
    !error.response && // if there is a response, it reached the server and not a network error
    error.code !== 'ECONNABORTED'; // check that it isn't a timeout

const retryCondition = (error: any) =>
    isNetworkError(error) || // my custom check
    axiosRetry.isIdempotentRequestError(error); // retry lib check

axiosRetry(Vue.axios, { retries: 3, retryDelay: axiosRetry.exponentialDelay, retryCondition });

// To make the base url globally accessible
const w = window as any;
w.BaseURL = Vue.axios.defaults.baseURL;

import User from './models/User';

// Initialize LTI status
{
    const param = getParameterByName('lti');
    if (param) 
    {
        store.commit('setIsLTI', true);

        const pidParam = getParameterByName('pid');
        if (pidParam) 
        {
            store.commit('setTargetLTIProjectId', pidParam);
        }
    }
}

Vue.set(window, "GetUser", GetUser);

async function Logout(clearRoute: boolean) 
{
    await ClearSession();
    if (clearRoute) 
    {
        router.push("/");
    }
    router.go(0);
}
Vue.set(window, "Logout", Logout);

// Attach local session id as auth token to all requests
axios.interceptors.request.use((cfg) => 
{
    if (cfg.url) 
    {
        if (cfg.url.startsWith('http://') || cfg.url.startsWith('https://')) 
        {
            console.log('Skipping auth for external Axios URL');
            return cfg;
        }
    }

    const sid = GetSID();
    if (sid != undefined) 
    {
        cfg.headers['Authorization'] = "Bearer " + sid;
    }
    return cfg;
});

// If bad request status and the error is an invalid sid,
// clear user data and refresh route to validate session ID
axios.interceptors.response.use(
    (resp) => 
    {
        //console.log("Received axios response w/ status == " + resp.status);
        if (resp.data.error != undefined &&
            (resp.data.error === "Invalid session") ||
            (resp.data.error === "Not signed in")) 
        {
            if (!router.currentRoute.meta.isLogin && !router.currentRoute.meta.isBase && !router.currentRoute.meta.canBeLogin) 
            {
                // refresh user data
                console.log("Refreshing things");
                store.commit('setUserData', null);
                router.go(0);
            }
        }
        return resp;
    },
    (err) => 
    {
        const error = err.response;
        //console.log("Received axios error w/ status == " + error.status);
        //console.log(JSON.stringify(error));
        if (error.status == 400 &&
            (
                (error.data.error == "Invalid session") ||
                (error.data.error == "Not signed in")
            )) 
        {
            if (!router.currentRoute.meta.isLogin && !router.currentRoute.meta.canBeLogin) 
            {
                // refresh user data
                console.log("Refreshing things");
                store.commit('setUserData', null);
                router.go(0);
            }
        }
        return Promise.reject(err);
    }
);

// For initial loading bar
Vue.set(window, "loadingUserData", false);

function handleLoginSuccess(src: any, result: any) 
{
    const caller = src;
    if (result.data.error != undefined) 
    {
        // if error, do nothing
        if (caller.errors != undefined)
            caller.errors.push({ message: result.data.error });
    }
    else 
    {
        // otherwise, redirect
        const data = result.data;
        if (result.data.sid != undefined) 
        {
            SetSID(result.data.sid);
        }
        router.push(caller.redirect);
    }
}

// Register a global custom directive called `v-focus`
Vue.directive('focus',
    {
        // When the bound element is inserted into the DOM...
        inserted: function (el) 
        {
            // Focus the element
            el.focus();
        },
        update: function (el, binding) 
        {
            // console.log(binding);
            if ((binding.value == undefined || binding.value) && binding.value != binding.oldValue) 
            {
                Vue.nextTick(function () 
                {
                    el.focus();
                });
            }
        }
    });

// Sets up globally available methods and user getter
Vue.mixin(
    {
        computed: {
            loadingUserData() 
            {
                return store.state.userdata == null && !((this.$route as any).meta.isLogin || (this.$route as any).meta.canBeLogin);
            },
            user() 
            {
                return store.state.userdata;
            }
        },
        methods:
        {
            dError(message: string) 
            {
                console.error(message);
            },
            dLog(message: string) 
            {
                console.log(message);
            },
            dAlert(message: string) 
            {
                alert(message);
            },
            handleLoginSuccess(src: any, result: any) 
            {
                return handleLoginSuccess(src, result);
            },
            login(src, form) 
            {
                const caller = src;
                axios.post("login", form).then(
                    (result) => 
                    {
                        handleLoginSuccess(src, result);
                    },
                    (error) => 
                    {
                        const result = error.response;
                        if (result.data.error != undefined) 
                        {
                            // if error, do nothing
                            if (caller.errors != undefined)
                                caller.errors.push({ message: result.data.error });
                        }
                    }
                ).then(
                    () => 
                    {
                        caller.attemptingLogin = false;
                    }
                );
            }
        }
    });

// If not logged in, then redirect to
// login page.  
router.beforeEach((to, from, next) => 
{
    if (to.meta.allowAnonymous) 
    {
        console.log('ANON');
        next();
        return;
    }

    if (GetUser() != null) 
    {
        if (to.meta != null) 
        {
            if (to.meta.isLogin) 
            {
                if (from && from.fullPath)
                    next(from.fullPath);
                else
                    next('/');
                return;
            }
            if (to.meta.requiresGlobalAdmin && !(GetUser().isAdmin)) 
            {
                next('/');
                return;
            }
        }
        next();
        return;
    }

    SessionIsValid((valid: boolean) => 
    {
        if (to.meta) 
        {
            if (to.meta.isLogin && valid) 
            {
                if (from && from.fullPath)
                    next(from.fullPath);
                else
                    next('/');
                return;
            }
            if (to.meta.requiresGlobalAdmin && GetUser() && !(GetUser().isAdmin)) 
            {
                next('/');
                return;
            }
            if (to.meta.canBeLogin) 
            {
                next();
                return;
            }
        }
        if (!(to.meta && to.meta.isLogin) && !valid) 
        {
            next("/login?redirect=" + to.fullPath);
            return;
        }
        next();
    });
});

new Vue({
    router,
    store,
    render: h => h(App)
}).$mount('#app');

window.onbeforeunload = function () 
{
    const e = window.event;
    w.isUnloading = true;
};